
export function initAsideDrawer(userOptions = {}) {
  const defaults = {
    headerOffset: 72,
    desktopMin: 1024,

    selectors: {
      toggle: '.faq-page__toggle',
      aside: '.faq-page__asside',
      close: '.faq-page__asside-top-close',
      navLink: '.faq-page__nav-link',
      navItem: '.faq-page__nav-item',
    },

    classes: {
      open: 'is-open',
      bodyLocked: 'overflow-hidden',
      active: 'active',
    },

    // якщо потрібно, щоб компонент шукав елементи не по всьому document,
    // а в конкретному контейнері
    root: document,
  };

  const opts = {
    ...defaults,
    ...userOptions,
    selectors: { ...defaults.selectors, ...(userOptions.selectors || {}) },
    classes: { ...defaults.classes, ...(userOptions.classes || {}) },
  };

  const { headerOffset, desktopMin, selectors, classes, root } = opts;

  const isDesktop = () =>
    window.matchMedia(`(min-width: ${desktopMin}px)`).matches;

  const qs = (sel, r = root) => r.querySelector(sel);
  const qsa = (sel, r = root) => Array.from(r.querySelectorAll(sel));

  const toggleBtn = qs(selectors.toggle);
  const asideEl = qs(selectors.aside);

  if (!toggleBtn || !asideEl) return () => {}; // safe noop destroy

  // Guard (щоб не інітилось двічі на тих самих елементах)
  if (asideEl.__asideDrawerInited) return asideEl.__asideDrawerDestroy || (() => {});
  asideEl.__asideDrawerInited = true;

  if (!asideEl.id) asideEl.id = `aside-${Math.random().toString(16).slice(2)}`;
  toggleBtn.setAttribute('aria-controls', asideEl.id);

  const isOpen = () => toggleBtn.getAttribute('aria-expanded') === 'true';

  const syncInert = () => {
    asideEl.inert = !isDesktop() && !isOpen();
  };

  const syncAsideAccessibility = (open = isOpen()) => {
    if (isDesktop() || open) {
      asideEl.removeAttribute('inert');
    } else {
      asideEl.setAttribute('inert', '');
    }
  };

  const setAsideState = (open) => {
    toggleBtn.setAttribute('aria-expanded', String(open));
    asideEl.classList.toggle(classes.open, open);
    syncAsideAccessibility(open);
    document.body.classList.toggle(classes.bodyLocked, open);
    syncInert();

    if (!open) toggleBtn.focus();
  };

  const setActiveNavLink = (hash) => {
    const links = qsa(selectors.navLink, asideEl);
    const items = qsa(selectors.navItem, asideEl);

    items.forEach((i) => i.classList.remove(classes.active));
    links.forEach((l) => {
      l.classList.remove(classes.active);
      l.removeAttribute('aria-current');
    });

    if (!hash) return;

    const activeLink = links.find((l) => l.getAttribute('href') === hash);
    if (!activeLink) return;

    activeLink.classList.add(classes.active);
    activeLink.setAttribute('aria-current', 'location');
    activeLink.closest(selectors.navItem)?.classList.add(classes.active);
  };

  const focusTarget = (target) => {
    const focusEl = target.querySelector('.faq-page__block-header') || target;

    if (!focusEl.hasAttribute('tabindex')) {
      focusEl.setAttribute('tabindex', '-1');
      focusEl.dataset.asideDrawerAddedTabindex = 'true';
    }

    focusEl.focus({ preventScroll: true });
  };

  const openAside = () => setAsideState(true);
  const closeAside = () => setAsideState(false);

  const onToggleClick = (e) => {
    if (isDesktop()) return;
    e.stopPropagation();
    isOpen() ? closeAside() : openAside();
  };

  const onAsideClick = (e) => {
    if (isDesktop()) return;

    const closeBtn = e.target.closest(selectors.close);
    if (closeBtn) {
      e.preventDefault();
      closeAside();
      return;
    }

    const link = e.target.closest(selectors.navLink);
    if (!link) return;

    const hash = link.getAttribute('href') || '';
    if (!hash.startsWith('#') || hash.length < 2) return;

    const target = document.getElementById(hash.slice(1));
    if (!target) return;

    e.preventDefault();
    setActiveNavLink(hash);

    if (isOpen()) closeAside();

    window.setTimeout(() => {
      const top =
        target.getBoundingClientRect().top +
        window.pageYOffset -
        headerOffset;

      window.scrollTo({
        top: Math.max(top, 0),
        behavior: 'smooth',
      });

      history.pushState(null, '', hash);
      focusTarget(target);

      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }, 50);
  };

  const onDocClick = (e) => {
    if (isDesktop() || !isOpen()) return;
    if (!asideEl.contains(e.target) && !toggleBtn.contains(e.target)) closeAside();
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    if (isDesktop() || !isOpen()) return;
    closeAside();
  };

  const onResize = () => {
    if (isDesktop()) {
      closeAside();
      syncAsideAccessibility(false);
    } else {
      syncAsideAccessibility();
    }
    if (isDesktop()) closeAside();
    else syncInert();
  };

  // bind
  toggleBtn.addEventListener('click', onToggleClick);
  asideEl.addEventListener('click', onAsideClick);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKeyDown);
  window.addEventListener('resize', onResize);

  // initial state
  syncInert();
  if (location.hash) setActiveNavLink(location.hash);
  syncAsideAccessibility();

  // destroy (для SPA / перевикористання / якщо DOM перерендерюється)
  const destroy = () => {
    toggleBtn.removeEventListener('click', onToggleClick);
    asideEl.removeEventListener('click', onAsideClick);
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('resize', onResize);

    // не чіпаю класи навмисно, але можна розкоментити, якщо треба “очищати”
    // asideEl.classList.remove(classes.open);
    // document.body.classList.remove(classes.bodyLocked);

    asideEl.__asideDrawerInited = false;
    asideEl.__asideDrawerDestroy = null;
  };

  asideEl.__asideDrawerDestroy = destroy;

  return destroy;
}
